'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Phone, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  identifier: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

const demoCredentials = [
  { role: 'Investor (KYC Approved)', email: 'arjun.mehta@zaxcorp.in', password: 'Demo@ZAX2026', userRole: 'investor' },
  { role: 'Investor (KYC Pending)', email: 'priya.sharma@zaxcorp.in', password: 'Demo@ZAX2026', userRole: 'investor' },
  { role: 'Operator / Admin', email: 'admin@zaxcorp.in', password: 'Admin@ZAX2026', userRole: 'operator' },
];

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [identifierMode, setIdentifierMode] = useState<'email' | 'phone'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { identifier: '', password: '', rememberMe: false },
  });

  const autofillCredentials = (cred: typeof demoCredentials[0]) => {
    setValue('identifier', cred.email, { shouldValidate: true });
    setValue('password', cred.password, { shouldValidate: true });
    toast.success(`Credentials filled for "${cred.role}"`);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.identifier, data.password);
      const role = result?.user?.user_metadata?.role ?? 'investor';
      toast.success('Welcome back! Signing you in…');
      if (role === 'operator') {
        router.push('/command-center');
      } else {
        router.push('/investor-home-dashboard');
      }
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message ?? 'Invalid credentials. Please try again.';
      setError('identifier', { message: msg });
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-700 text-foreground">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your ZAX-CORP account</p>
      </div>

      {/* Identifier mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-border">
        {(['email', 'phone'] as const).map((mode) => (
          <button
            key={`id-mode-${mode}`}
            type="button"
            onClick={() => {
              setIdentifierMode(mode);
              setValue('identifier', '');
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-600 transition-all duration-150 ${
              identifierMode === mode
                ? 'bg-primary/15 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            {mode === 'email' ? <Mail size={13} /> : <Phone size={13} />}
            {mode === 'email' ? 'Email' : 'Mobile'}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Identifier */}
        <div>
          <label htmlFor="identifier" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            {identifierMode === 'email' ? 'Email Address' : 'Mobile Number'}
          </label>
          <input
            id="identifier"
            type={identifierMode === 'email' ? 'email' : 'tel'}
            className="input-field"
            placeholder={identifierMode === 'email' ? 'you@example.in' : '+91 98765 43210'}
            autoComplete={identifierMode === 'email' ? 'email' : 'tel'}
            {...register('identifier', {
              required: identifierMode === 'email' ? 'Email is required' : 'Mobile number is required',
              pattern:
                identifierMode === 'email'
                  ? { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' }
                  : { value: /^(\+91)?[6-9]\d{9}$/, message: 'Enter a valid Indian mobile number' },
            })}
          />
          {errors.identifier && (
            <p className="mt-1.5 text-xs text-danger" role="alert">{errors.identifier.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-xs font-600 text-muted-foreground tracking-wide uppercase">
              Password
            </label>
            <button type="button" className="text-xs text-primary hover:underline">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="input-field pr-10"
              placeholder="Enter your password"
              autoComplete="current-password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-danger" role="alert">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="rememberMe"
            type="checkbox"
            className="w-4 h-4 rounded accent-primary cursor-pointer"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer select-none">
            Keep me signed in for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full h-11"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in…
            </>
          ) : (
            'Sign In to ZAX-CORP'
          )}
        </button>
      </form>

      {/* Switch to register */}
      <p className="text-center text-sm text-muted-foreground">
        New to ZAX-CORP?{' '}
        <button type="button" onClick={onSwitchToRegister} className="text-primary font-600 hover:underline">
          Create your account
        </button>
      </p>

      {/* Demo credentials box */}
      <div className="mt-2 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.06)' }}>
        <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: 'rgba(139,92,246,0.2)' }}>
          <span className="w-2 h-2 rounded-full bg-violet" />
          <span className="text-xs font-700 tracking-wide uppercase text-violet">Demo Credentials</span>
        </div>
        <div className="p-3 space-y-2.5">
          {demoCredentials.map((cred, i) => (
            <div
              key={`demo-cred-${i + 1}`}
              className="rounded-lg p-3 transition-all duration-150 cursor-pointer hover:bg-violet/10"
              style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.15)' }}
              onClick={() => autofillCredentials(cred)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-600 text-violet">{cred.role}</span>
                <span className="text-xs text-muted-foreground">Click to autofill →</span>
              </div>
              <div className="space-y-1">
                <p className="font-mono text-xs text-muted-foreground">{cred.email}</p>
                <p className="font-mono text-xs text-muted-foreground">{cred.password}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}